<template>
  <div>
    <v-container class="pa-0" fluid>
      <v-card color="transparent" class="hyp-card" flat>
        <DetailsSummary
          :features="initialFeatures"
          :title="title"
        ></DetailsSummary>

        <v-row
          v-if="showSourcesSelector"
          row
          class="pa-2 hyp-details-list-bg mx-0 mt-3"
        >
          <v-col class="col-12 pa-0">
            <v-container fluid class="pa-0">
              <DetailsSourceSelector
                class="pull-right"
                @click="sourceChanged"
                ref="detailsSourceSelector"
                :includeIncidents="true"
                :selected="selectedSource || 'news'"
              ></DetailsSourceSelector>
              <div class="clearfix"></div>
            </v-container>
          </v-col>
        </v-row>

        <DetailsListCluster
          v-if="items && items.length"
          @get-data="getContent"
          :items="items"
          :page="page"
          :pageSize="pageSize"
          :height-pixels="heightPixels"
          class="mt-3"
          :loader="isLoading"
          :matomo-category="matomoCategory"
          :action-icons-enabled="actionIconsEnabled"
          :show-primary-topics="true"
          :show-thumbs-down="showThumbsDown"
          :get-threshold-type-function="getThresholdTypeFunction"
          :get-params-function="getParamsFunction"
          @read-click="handleReadClick"
          @unread-click="handleUnreadClick"
          @loaded="listLoading = false"
        ></DetailsListCluster>

        <Loader v-if="isLoading" text="" color="white"></Loader>

        <div
          v-if="!isLoading && (!items || !items.length)"
          class="pa-3 hyp-details-list"
        >
          No articles found matching search criteria
        </div>
        <DetailsSearchBar
          v-show="!isLoading && items && showSearchBar"
          :type="detailsSidebar.type"
        ></DetailsSearchBar>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashOrderBy from "lodash/orderBy";

import { mapGetters } from "vuex";
import { GET_USER_ALERTED_CONTENT_ACTION } from "@/store/actions.type";
import { MODULE_PREFIX_ALERTS_CONTENT } from "@/store";
import {
  hasTwitterAccess,
  hasEssentialsAccess,
} from "@/services/user/user.service";
import { setInfiniteScrollState } from "@/common/basic.utilities";
import { nowUtc, getFormatted } from "@/common/date.utilities";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import {
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_SEARCH,
} from "@/store";
import {
  getTopicsArray,
  getInfluencesArray,
  getTopicKey,
} from "@/services/events/topics.service";
import {
  ALERTED_CONTENT_ACTION_READ,
  ALERTED_CONTENT_ACTION_UNREAD,
} from "@/common/constants/alert.js";
import { getFeedbackLoopItem } from "@/common/browserDb/feedbackLoop";
import lodashHas from "lodash/has";
import { EVENT_CATEGORIES } from "@/common/constants/analytics";
import { SOCIAL_SOURCES } from "@/common/constants/wodSocialSources.js";

const NON_TOPIC_SOURCES = ["news", "social", "incidents"];

export default {
  name: "MapClickDetails",
  components: {
    Loader: () => import("@/components/common/Loader"),
    DetailsSummary: () => import("@/components/common/details/DetailsSummary"),
    DetailsListCluster: () =>
      import("@/components/common/details/DetailsListCluster"),
    DetailsSourceSelector: () =>
      import("@/components/common/details/DetailsSourceSelector"),
    DetailsSearchBar: () =>
      import("@/components/common/details/DetailsSearchBar"),
  },
  data: () => ({
    page: 1,
    pageSize: 50,
    contentFeatures: [],
    keywords: null,
    listLoading: true,
    initialFeatures: [], //A fixed set of features to prevent the location from recalculating while paging
    localSearchDetails: null,
    selectedSource: null,
    matomoCategory: EVENT_CATEGORIES.map,
    attemptMade: false,
    hasEssentialsAccess: hasEssentialsAccess(),
  }),
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      searchType: MODULE_PREFIX_SEARCH + "type",
      searchDetails: MODULE_PREFIX_SEARCH + "searchDetails",
      loading: MODULE_PREFIX_SEARCH + "loading",
    }),
    getParamsFunction() {
      return lodashGet(this.detailsSidebar, "getParamsFunction");
    },
    showSourcesSelector() {
      if (hasTwitterAccess()) {
        let exists = false;
        if (this.type) {
          exists = !!this.getCurrentTopic();

          const INFLUENCES_ARRAY = getInfluencesArray();
          INFLUENCES_ARRAY.forEach((topic) => {
            if (topic) {
              if (
                topic.apiName &&
                topic.apiName.toLowerCase() === this.type.toLowerCase()
              ) {
                exists = true;
              }
            }
          });
        }
        return exists;
      }
      return false;
    },
    type() {
      if (
        this.detailsSidebar &&
        this.detailsSidebar.type &&
        this.detailsSidebar.type !== DETAIL_SIDEBAR_TYPES.stability
      ) {
        return this.detailsSidebar.type;
      }
      return null;
    },
    title() {
      if (this.detailsSidebar && this.detailsSidebar.title) {
        return this.detailsSidebar.title.toUpperCase();
      }
      return this.type;
    },
    total() {
      if (this.detailsSidebar.total) {
        return this.detailsSidebar.total;
      }
      return this.detailsSidebar.features.length || 0;
    },
    features() {
      let features = [];
      if (lodashGet(this.detailsSidebar, "features")) {
        features = lodashOrderBy(
          this.detailsSidebar.features,
          (item) => item.properties.formatted.occurred,
          ["desc"]
        );
      }
      return features;
    },
    mapId() {
      return `map-${this._uid}`;
    },
    iconColor() {
      return this.detailsSidebar.iconColor;
    },
    iconName() {
      return this.detailsSidebar.iconName;
    },
    items() {
      return this.contentFeatures;
    },
    isLoading() {
      if (this.listLoading) {
        return true;
      }
      if (this.loading) {
        return true;
      }
      return false;
    },
    heightPixels() {
      return this.$parent.$el.offsetHeight - 170;
    },
    nowFormatted() {
      return getFormatted(nowUtc());
    },
    actionIconsEnabled() {
      if (this.detailsSidebar.type === DETAIL_SIDEBAR_TYPES.alerts) {
        return true;
      }
      return false;
    },
    showThumbsDown() {
      if (lodashGet(this, "detailsSidebar.type") === "social") {
        return false;
      }
      return true;
    },
    showSearchBar() {
      if (lodashGet(this, "detailsSidebar.hideSearchBar")) {
        return false;
      }
      return true;
    },
  },
  mounted() {
    this.getContent();
  },
  methods: {
    async getContent(page, $state) {
      this.listLoading = true;

      let FEATURES = [];

      page = !page ? 1 : page;

      let paramsFunc = lodashGet(this, "detailsSidebar.getParamsFunction"),
        params;
      if (paramsFunc) {
        params = paramsFunc();
      }

      if (!params) {
        const PAGE_OFFSET = (page - 1) * this.pageSize;
        FEATURES = this.features.slice(
          PAGE_OFFSET,
          PAGE_OFFSET + this.pageSize
        );
        setInfiniteScrollState($state, { features: FEATURES }, "features");
        this.page = page;
      } else {
        params.noUpdateMap = true;

        //If no source filter is selected, set it to whatever the user searched for in live search
        if (!this.selectedSource) {
          if (params.sources.includes("news") || !params.sources) {
            //News is the preferred selected tab
            this.selectedSource = "news";
          } else if (
            params.eventType === "all" ||
            params.eventType === "detected"
          ) {
            //For all searches without news, select the social tab
            this.selectedSource = "social";
          } else if (
            params.sources.includes("maritime") ||
            params.sources.includes("political")
          ) {
            this.selectedSource = "incidents";
          }
        }

        // Override the sources param with the source type the user has selected
        if (this.selectedSource === "news") {
          params.sources = "news";
        } else if (this.selectedSource === "social") {
          params.sources = SOCIAL_SOURCES.join(",");
        } else if (this.selectedSource === "incidents") {
          //If the user selected verified events, only keep the maritime and political sources
          let verifiedSources = [];
          if (params.sources.indexOf("maritime") > -1) {
            verifiedSources.push("maritime");
          }
          if (params.sources.indexOf("political") > -1) {
            verifiedSources.push("political");
          }
          params.sources = verifiedSources.join(",");
          if (!params.sources) {
            params.sources = "maritime,political";
          }
        }

        //Default sources
        if (!params.sources) {
          if (this.hasEssentialsAccess) {
            params.sources = "maritime,political";
          } else {
            params.sources = "news";
          }
        }

        //Filter search to the place where the user clicked
        if (lodashGet(this, "detailsSidebar.SelectorSourceRef.clickBounds")) {
          params.bounds = lodashGet(
            this,
            "detailsSidebar.SelectorSourceRef.clickBounds"
          );
        }

        //Override the topic, if there is one
        let topic = this.getCurrentTopic();
        if (topic && topic.apiName) {
          params.topics = topic.apiName;
        }

        if (this.searchDetails) {
          params.searchDetails = this.searchDetails;
        } else {
          delete params.searchDetails;
        }
        await this.detailsSidebar.SelectorSourceRef.getData(params, page);

        const dataType = lodashGet(
          this,
          "detailsSidebar.SelectorSourceRef.type"
        );

        let storePath;
        if (NON_TOPIC_SOURCES.indexOf(dataType) > -1) {
          storePath = `$store.state[${dataType}][${dataType}].features`;
          FEATURES = lodashGet(this, storePath) || [];
        } else if (
          lodashGet(this, "detailsSidebar.SelectorSourceRef.features")
        ) {
          FEATURES = this.detailsSidebar.SelectorSourceRef.features;
        }
      }

      if (page === 1) {
        this.initialFeatures = FEATURES;
      }

      let exists = false;
      if (this.type) {
        exists = !!this.getCurrentTopic();
      }

      const INFLUENCES_ARRAY = getInfluencesArray();
      INFLUENCES_ARRAY.forEach((topic) => {
        if (topic) {
          if (
            topic.apiName &&
            this.type &&
            topic.apiName.toLowerCase() === this.type.toLowerCase()
          ) {
            exists = true;
          }
        }
      });
      if (exists) {
        await this.checkThumbsDownFeedback(FEATURES);
      }

      if (
        this.contentFeatures &&
        Array.isArray(this.contentFeatures) &&
        this.contentFeatures.length &&
        page !== 1
      ) {
        this.contentFeatures = this.contentFeatures.concat(FEATURES);
      } else {
        this.contentFeatures = FEATURES;
      }

      if (
        !this.attemptMade &&
        !this.selectedSource &&
        this.contentFeatures.length === 0 &&
        !lodashHas(params, "eventType")
      ) {
        this.attemptMade = true;
        this.$refs.detailsSourceSelector.toggle("social");
        return;
      }

      if (
        !this.attemptMade &&
        lodashHas(params, "eventType") &&
        params.eventType === "verified" &&
        this.contentFeatures.length === 0
      ) {
        this.attemptMade = true;
        this.$refs.detailsSourceSelector.toggle("incidents");
        return;
      }

      setInfiniteScrollState($state, { features: FEATURES }, "features");

      this.page = page;
      this.listLoading = false;
    },
    async checkThumbsDownFeedback(features) {
      if (features && Array.isArray(features)) {
        const LEN = features.length;
        for (let i = 0; i < LEN; i++) {
          let feature = features[i];
          if (lodashGet(feature, "properties.formatted.esId")) {
            const RESULT = await getFeedbackLoopItem({
              eventType: this.type ? this.type.toLowerCase() : null,
              esId: feature.properties.formatted.esId,
            });
            if (RESULT && !RESULT.thumbsUp) {
              feature.properties.formatted.thumbsDown = true;
            }
          }
        }
      }
    },
    handleReadClick(id) {
      this.updateFeatureRead(id, true);
      this.$store.dispatch(
        MODULE_PREFIX_ALERTS_CONTENT + GET_USER_ALERTED_CONTENT_ACTION,
        {
          action: ALERTED_CONTENT_ACTION_READ,
          id: id,
        }
      );
    },
    handleUnreadClick(id) {
      this.updateFeatureRead(id, false);
      this.$store.dispatch(
        MODULE_PREFIX_ALERTS_CONTENT + GET_USER_ALERTED_CONTENT_ACTION,
        {
          action: ALERTED_CONTENT_ACTION_UNREAD,
          id: id,
        }
      );
    },
    updateFeatureRead(id, read) {
      this.detailsSidebar.features.forEach((feature) => {
        if (feature.properties.formatted.documentId === id) {
          feature.properties.formatted.read = read;
        }
      });
    },
    sourceChanged(type) {
      this.selectedSource = type;
      this.contentFeatures = [];
      this.getContent();
    },
    getThresholdTypeFunction(doc) {
      let thresholdType = "cluster";
      if (
        lodashGet(doc, "properties.formatted.source") === "twitter.com" ||
        lodashGet(doc, "properties.formatted.source") === "twitter"
      ) {
        thresholdType = "twitter";
      }
      return thresholdType;
    },
    getCurrentTopic() {
      const TOPICS_ARRAY = getTopicsArray();
      let currentTopic;

      let i = 0;
      while (!currentTopic && i < TOPICS_ARRAY.length) {
        let topic = TOPICS_ARRAY[i];
        if (topic) {
          const field =
            topic.parentIds && topic.parentIds[0] ? topic.parentIds[0] : "type";
          const type = topic[field];
          const key = getTopicKey(type, false);
          if (key && key.toLowerCase() === this.type.toLowerCase()) {
            currentTopic = topic;
          }
        }
        i++;
      }

      return currentTopic;
    },
  },
  watch: {
    searchDetails: {
      async handler() {
        if (this.localSearchDetails === this.searchDetails) {
          return;
        }
        this.localSearchDetails = this.searchDetails;
        this.contentFeatures = [];
        this.getContent();
        this.attemptMade = false;
      },
    },
  },
  beforeDestroy() {
    this.contentFeatures = null;
    this.listLoading = false;
  },
};
</script>

<style scoped>
.hyp-card {
  background-color: transparent !important;
}
.hyp-header {
  background-color: #191a1a !important;
}
.hyp-date-formatted {
  font-size: 12px;
}
.hyp-details-list {
  height: 82vh;
  padding: 15px !important;
  background-color: #191a1a !important;
  position: relative;
  margin-top: 16px !important;
  margin-left: 8px !important;
}
.hyp-details-list-bg {
  background-color: #191a1a !important;
}
</style>
